import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../Context/AuthContext'
import DarkAuthInput from '../../Components/Inputs/DarkAuthInput'
import logo from '../../Assets/new-logo.png'
import { Mail } from 'react-feather'

const ForgotPasswordScreen = () => {
  const { resetUserEmail } = useAuth()
  const [email, setEmail] = useState<string>('')
  const [emailSent, setEmailSent] = useState<boolean>(false)

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const sendResetEmail = () => {
    resetUserEmail(email)
    setEmailSent(true)
  }

  return (
    <div className="min-h-screen w-full bg-dark-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-accent-purple/20 rounded-full blur-3xl -translate-y-1/2 animate-float" />
      <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-accent-gold/15 rounded-full blur-3xl translate-y-1/2 animate-float-slow" />
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-accent-pink/10 rounded-full blur-3xl -translate-x-1/2 animate-float-slower" />

      {/* Main card */}
      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo and header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-dark-card rounded-2xl border border-dark-border mb-4 animate-bounce-subtle">
            <img className="h-14 w-14 object-contain" src={logo} alt="Logo" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Forgot Password</h1>
          <p className="text-gray-400">We'll send you a reset link to your email</p>
        </div>

        {/* Forgot password form card */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-8 shadow-2xl animate-scale-in">
          {emailSent ? (
            // Success state
            <div className="text-center animate-fade-in">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-green-500/10 rounded-2xl flex items-center justify-center border border-green-500/20 animate-pulse-slow">
                  <Mail className="w-10 h-10 text-green-400" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Check your email</h3>
              <p className="text-gray-400 text-sm mb-6">
                We've sent a password reset link to <span className="text-white">{email}</span>
              </p>
              <Link
                to="/auth/confirmResetPassword"
                className="block w-full py-3.5 px-4 bg-gradient-to-r from-accent-teal to-accent-deep-purple
                           text-white font-semibold rounded-xl text-center
                           hover:opacity-90 hover:scale-[1.02] transition-all duration-200
                           shadow-lg shadow-accent-teal/25 hover:shadow-xl hover:shadow-accent-teal/30"
              >
                Enter Reset Code
              </Link>
            </div>
          ) : (
            // Form state
            <>
              {/* Mail icon */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-accent-purple/20 to-accent-gold/20 rounded-2xl flex items-center justify-center border border-dark-border animate-pulse-slow">
                  <Mail className="w-10 h-10 text-accent-teal" />
                </div>
              </div>

              <DarkAuthInput
                value={email}
                handleFunction={handleEmailChange}
                placeholder="Enter your email address"
                type="email"
                icon="email"
              />

              <button
                onClick={sendResetEmail}
                disabled={!email}
                className="w-full py-3.5 px-4 bg-gradient-to-r from-accent-teal to-accent-deep-purple
                           text-white font-semibold rounded-xl
                           hover:opacity-90 hover:scale-[1.02] transition-all duration-200
                           disabled:opacity-50 disabled:cursor-not-allowed
                           shadow-lg shadow-accent-teal/25 hover:shadow-xl hover:shadow-accent-teal/30 mt-2"
              >
                Send Reset Link
              </button>
            </>
          )}

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-dark-border" />
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-dark-border" />
          </div>

          {/* Back to login link */}
          <Link to="/" className="block text-center">
            <span className="text-accent-gold hover:text-accent-gold-light transition-colors text-sm font-medium">
              Back to Login
            </span>
          </Link>
        </div>

        {/* Help note */}
        <div className="mt-6 flex justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="bg-dark-card/50 rounded-xl px-5 py-2.5 border border-dark-border hover:border-accent-teal/30 transition-colors duration-300">
            <p className="text-gray-500 text-xs text-center">
              Didn't receive the email? Check your spam folder
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordScreen
